import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  CriterionCardChrome,
  CriterionDefinitionField,
  CriterionLabelField,
  CriterionStableKeyField,
  type CriterionCardChromeProps,
} from '@/components/admin/evaluation-rubrics/criterion-card-chrome'
import type { RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'
import { slugifyCriterionKey } from '@/lib/rubric-publish-payload'

type RubricRequirementCardProps = Omit<CriterionCardChromeProps, 'children' | 'footer'> & {
  onUpdate: (patch: Partial<RubricCriterion>) => void
  depth?: 'full' | 'advanced'
}

export function RubricRequirementCard({
  row,
  errors,
  onUpdate,
  depth = 'full',
  ...chromeProps
}: RubricRequirementCardProps) {
  const [advancedOpen, setAdvancedOpen] = useState(depth === 'advanced')

  if (depth === 'advanced') {
    return (
      <CriterionCardChrome row={row} errors={errors} {...chromeProps}>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium">{row.label || row.key}</p>
          <Badge variant="outline">Pass / fail</Badge>
        </div>
        {row.definition ? (
          <p className="text-sm text-muted-foreground">{row.definition}</p>
        ) : null}
        <CriterionStableKeyField row={row} error={errors?.key} onUpdate={onUpdate} />
      </CriterionCardChrome>
    )
  }

  return (
    <CriterionCardChrome row={row} errors={errors} {...chromeProps}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <CriterionLabelField
            row={row}
            error={errors?.label}
            onUpdate={(patch) => {
              onUpdate(patch)
              if (patch.label?.trim()) {
                const autoKey = slugifyCriterionKey(patch.label, 'requirement')
                if (!row.key || row.key.startsWith('req_')) {
                  onUpdate({ ...patch, key: autoKey })
                }
              }
            }}
          />
        </div>
        <Badge variant="outline" className="mt-6 shrink-0">
          Pass / fail
        </Badge>
      </div>
      <CriterionDefinitionField row={row} onUpdate={onUpdate} />
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto px-0"
          onClick={() => setAdvancedOpen((o) => !o)}
        >
          {advancedOpen ? (
            <ChevronDown className="mr-1 h-4 w-4" aria-hidden />
          ) : (
            <ChevronRight className="mr-1 h-4 w-4" aria-hidden />
          )}
          Advanced
        </Button>
        {advancedOpen ? (
          <div className="pt-2">
            <CriterionStableKeyField row={row} error={errors?.key} onUpdate={onUpdate} />
          </div>
        ) : null}
      </div>
    </CriterionCardChrome>
  )
}
